<template>
  <div class="white-bg p3 m3">
    <div class="mb2 mt-1 ml-1">
      <span v-if="formName" class="bold">{{ formName }}</span>
    </div>
    <div v-for="(line, lineIndex) in form" :key="lineIndex" class="flex flex-wrap inputs-holder">
      <div v-for="(column, columnIndex) in line" :key="columnIndex" class="relative px1" :class="column.class">
        <!-- Simple Field -->
        <template v-if="simpleField.includes(column.type)">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative col-12 mb1">
            <input
              v-if="column.id == 'birth_date'"
              :id="column.id"
              v-model="data[column.id]"
              v-mask="'##/##/####'"
              type="text"
              :disabled="column.disabled"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :placeholder="getReferencelabel(column.placeholder)"
            >
            <input
              v-else
              :id="column.id"
              :ref="column.id"
              :value="getProperty(data, column.id)"
              :type="column.type"
              :disabled="column.disabled"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :placeholder="getReferencelabel(column.placeholder)"
              @input="setProperty($event, column.id)"
            >
          </div>
        </template>

        <template v-else-if="column.type === 'address'">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <input
            :id="column.id"
            v-model="data[column.id]"
            type="text"
            :disabled="column.disabled"
            class="border col-12 pl2 input-height"
            :class="{'invalid-field': allFieldsValid[column.id]}"
            :placeholder="getReferencelabel(column.placeholder)"
          >
        </template>

        <template v-else-if="column.type === 'tel'">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative col-12 mb1 z3">
            <vue-tel-input
              :id="column.id"
              :ref="column.id"
              v-model="data[column.id]"
              default-country="FR"
              mode="international"
              :preferred-countries="['FR']"
              :type="column.type"
              :disabled="column.disabled"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :input-options="{
                placeholder: getReferencelabel(column.placeholder)
              }"
              @validate="handleValidation"
            />
          </div>
        </template>

        <template v-else-if="column.type === 'em_tel'">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative col-12 mb1 z3">
            <vue-tel-input
              :id="column.id"
              :ref="column.id"
              v-model="emergencyContactComputed"
              default-country="FR"
              mode="international"
              :preferred-countries="['FR']"
              :type="column.type"
              :disabled="column.disabled"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :input-options="{
                placeholder: getReferencelabel(column.placeholder)
              }"
              @validate="handleValidation"
            />
          </div>
        </template>

        <!-- Select Field -->
        <template v-else-if="column.type === 'select'">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative border white-bg col-12 mb1">
            <select :ref="column.id" :value="getProperty(data, column.id)" class="border-none relative z2 col-12 pl2 input-height" :class="{'select-disabled': column.disabled}" @input="setProperty($event, column.id)">
              <option v-if="column.placeholder" :value="null" disabled selected>{{ getReferencelabel(column.placeholder) }}</option>
              <option v-for="(option, optionIndex) in column.options" :key="optionIndex" :disabled="column.disabled" :value="option.value">{{ option.text }}</option>
            </select>
            <span class="absolute red right-0 flex items-center mr1 top-0 bottom-0">
              <i class="icon mt1 icon-chevron-down"></i>
            </span>
          </div>
        </template>

        <!-- Info Label -->
        <template v-else-if="column.type === 'info'">
          <p :id="column.id" class="center col-12" style="padding: .625rem 1.875rem; font-size: .8125rem; background-color: #e6ecf2;">{{ getReferencelabel(column.reference) }}</p>
        </template>

        <!-- Radio Group -->
        <template v-else-if="column.type === 'radio'">
          <label class="h6n bold" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
          <div class="col-12">
            <div v-for="(option, optionIndex) in column.options" :key="optionIndex" class="mr1 inline-block">
              <input :id="column.id + option.value" v-model="data[column.id]" type="radio" :name="column.id" :disabled="column.disabled" :value="option.value">
              <label :class="column.disabled ? 'disabled' : 'pointer'" :for="column.id + option.value">{{ option.text }}</label>
            </div>
          </div>
        </template>

        <!-- Checkbox -->
        <template v-else-if="column.type === 'checkbox'">
          <input :id="column.id" v-model="data[column.id]" type="checkbox" :name="column.id" :disabled="column.disabled">
          <label class="h6n" :for="column.id">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></label>
        </template>

        <!-- Link -->
        <template v-else-if="column.type === 'link'">
          <div v-if="!column.disabled" class="col-12">
            <span class="pointer" :class="column.class" @click="column.target">{{ getReferencelabel(column.reference) }}<span v-if="column.required" class="red"> *</span></span>
          </div>
        </template>
        <p v-if="allFieldsValid[column.id]" class="error-message warning mb2">{{ getReferencelabel(allFieldsValid[column.id]) }}</p>
      </div>
    </div>
    <div v-if="visibleActions" class="col-12 flex flex-wrap-reverse py2 justify-center">
      <Button reference="CANCEL" class="col-12 p1" :class=" deleteAction ? 'sm-col-4' : 'sm-col-6'" btn-color="blue" :action="goBack" />
      <Button v-if="deleteAction" reference="DELETE" class="col-12 sm-col-4 p1" btn-color="blue" :action="deleteAction" />
      <Button :reference="buttonName" class="col-12 p1" :class=" deleteAction ? 'sm-col-4' : 'sm-col-6'" :disabled="!isObjectEmpty(allFieldsValid)" button-color="red-bg" :action="saveAction" />
    </div>
  </div>
</template>

<script>

import { parse, differenceInCalendarYears } from 'date-fns';
import { computed, watch, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { VueTelInput } from 'vue-tel-input';

import _ from 'lodash';

import Button from '@/components/elements/Button';

import 'vue-tel-input/dist/vue-tel-input.css';

import { isObjectEmpty, matchExact, lengthBetween, findRouteInformation, getReferencelabel } from '@/mixins/utils';

export default {
  components: { Button, VueTelInput },
  props: {
    form: {
      type: Array,
      default: () => []
    },
    data: {
      type: Object,
      default: () => {}
    },
    visibleActions: {
      type: Boolean,
      default: true
    },
    value: {
      type: Boolean,
      default: true
    },
    saveAction: {
      type: Function,
      default: () => { console.log('It works'); }
    },
    deleteAction: { type: Function },
    buttonName: {
      type: String,
      default: 'SAVE'
    },
    formName: {
      type: String,
      default: null,
      required: false
    }
  },
  setup (props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const mobileValidation = ref({});

    const routeInfo = computed(() => findRouteInformation(route.name));

    function setProperty (event, fieldName) {
      _.set(props.data, fieldName, event.target.value);
    }

    function getProperty (object, property) {
      return _.get(object, property);
    }

    const emergencyContactComputed = computed({
      // getter
      get () {
        return getProperty(props.data, 'emergency_contact.telephone');
      },
      // setter
      set (newValue) {
        // Note: we are using destructuring assignment syntax here.
        _.set(props.data, 'emergency_contact.telephone', newValue);
      }
    });

    function goBack () {
      router.replace({ name: routeInfo.value.parentName });
    }

    function handleValidation ($event) {
      mobileValidation.value = $event;
    }
    const simpleField = ['text', 'email', 'number', 'password', 'date', 'search', 'url', 'time', 'month', 'week'];
    const allFieldsValid = computed(() => {
      const errors = {};

      props.form.map(line => {
        line.map(column => {
          // Ignore info types
          if (['info', 'link'].includes(column.type)) {
            return;
          }

          if (column.required && props.data[column.id] === null || column.required && props.data[column.id] === undefined) { // When we open the form, the value is undefined or null, we put an global error and not the error in the field itself
            errors.firstTime = true;
            return;
          }

          // Global validations
          const globalValidation = checkError(props.data, column.id, mobileValidation.value.valid, column.required);
          if (globalValidation) {
            errors[column.id] = globalValidation;
          }
        });
      });

      return errors;
    });

    watch(allFieldsValid, errors => {
      emit('update:modelValue', isObjectEmpty(errors));
    });

    return { goBack, allFieldsValid, isObjectEmpty, simpleField, getReferencelabel, handleValidation, mobileValidation, setProperty, getProperty, emergencyContactComputed };
  }
};

function checkError (data, type, mobileValidation = false, required = false) {
  const value = data[type];
  let age;
  const dateInstance = new Date();

  // Not required and doesn't have value
  if (!required && !value) {
    return null;
  }

  switch (type) {
    case 'gender':
      // return !value ? 'Veuillez renseigner la civilité' : false;
      return !value ? '' : null;

    case 'firstname':
      if (required && value === '') {
        return 'FIRST_NAME_ERROR';
      }

      if (value && value.length < 2) {
        return 'FIRST_NAME_MORE_2';
      }

      return !matchExact(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value) ? 'FIRST_NAME_ONLY_LETTERS' : false;

    case 'lastname':
      if (required && value === '') {
        return 'LASTNAME_ERROR';
      }

      if (value && value.length < 2) {
        return 'LASTNAME_MORE_2';
      }

      return !matchExact(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value) ? 'LASTNAME_ONLY_LETTERS' : false;

    case 'birth_date':
      if (required && value === '') {
        return 'ERROR_BIRTH_DATE';
      }

      age = differenceInCalendarYears(dateInstance, parse(value, 'dd/MM/yyyy', dateInstance));

      if (!Number(age)) {
        return 'ERROR_DATE_FORMAT';
      }

      if (age < 0 || age > 100) {
        return 'BIRTH_DATE_INCORRECT';
      }

      return false;

    case 'email':
      if (required && value === '') {
        return 'ERROR_EMAIL_EMPTY';
      }

      return !matchExact(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/, value) ? 'ERROR_WRONG_EMAIL' : false;

    case 'mobilephone':
      if (required && value === '') {
        return 'ERROR_PHONE_EMPTY';
      }

      if (!matchExact(/^([0-9+\s]{8,})$/, value)) {
        return 'ERROR_ONLY_NUMBERS';
      }

      return !mobileValidation ? 'VALID_NUMBER' : false;

    case 'town':
      if (!lengthBetween(value, 1, 30)) {
        return 'ERROR_CITY_1_30';
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'ERROR_CITY_ONLY_LETTERS_AND_NUMBERS';
      }

      if (data.country === 'FR' && !matchExact(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff]*)$/, value)) {
        return 'ERROR_CITY_ONLY_LETTERS_FRANCE';
      }

      return false;

    case 'street_name':
      if (required && value === '') {
        return 'ERROR_STREET_NAME';
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'ERROR_CITY_ONLY_LETTERS_AND_NUMBERS';
      }

      return !lengthBetween(value, 1, 30) ? 'ERROR_CITY_1_30' : null;

    case 'street_number':
      if (required && value === '') {
        return 'ERROR_STREET_NUMBER';
      }

      return !matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){1,}$/, value) ? 'ERROR_STREET_NUMBERS_ONLY' : null;

    case 'address2':
      if (required && value === '') {
        return 'ERROR_ADDITIONAL_ADDRESS';
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s.,]*){2,}$/, value)) {
        return 'ERROR_ADDRESS_LETTERS_CHARACTERS';
      }

      return !lengthBetween(value, 0, 30) ? 'ERROR_MAILING_ADDRESS' : null;

    case 'zip_code':
      if (required && value === '') {
        return 'ERROR_POSTAL_CODE';
      }

      if (data.country === 'FR' && !matchExact(/^([0-9]){5}$/, value)) {
        return 'ERROR_POSTAL_CODE_FRANCE';
      }

      return !matchExact(/^([A-Z a-z0-9]){3,10}$/, value) ? 'ERROR_POSTAL_CODE_NUMBERS' : null;

    case 'country':
      return !value ? 'ERROR_COUNTRY' : null;

    case 'nationality':
      return !value ? 'ERROR_NATIONALITY' : null;
    default:
      break;
  }

  return null;
}
</script>

<style>
.invalid-field {
  border: 1px solid red;
}
</style>
